/**
 * ry-pagination — port of daisyUI's `join`-based pagination pattern.
 *
 * Upstream: https://github.com/saadeghi/daisyui (MIT)
 *           daisyUI 5.5.20 — composed from utilities/join.css + components/button.css
 *
 * daisyUI ships pagination as a CSS composition (`join` group + `btn` row +
 * `btn-active` marker); the JS surface here is original work that wraps that
 * visual contract with a renderer, keyboard nav, and an event surface.
 *
 * Vendored reference: demos/parity/vendor/daisy/pagination/component.css
 *
 * @component ry-pagination
 * @category  nav
 * @summary   Page navigator with prev / next arrows, first-page boundary, sliding window of siblings around the current page, ellipses where the window jumps, and last-page boundary. Click a number to jump, click an arrow to step, or press Arrow Left / Right to walk pages. Emits `ry:change` whenever the page actually changes.
 *
 * @attribute total      number    default=1                Total number of pages. Must be >= 1.
 * @attribute page       number    default=1                Current page (1-indexed, clamped to [1, total]).
 * @attribute siblings   number    default=1                Page numbers shown on EACH side of the current page.
 * @attribute boundary   number    default=1                Page numbers always shown at the very start and very end.
 * @attribute size       'xs' | 'sm' | 'md' | 'lg' | 'xl'   default='md'   Visual size of the buttons.
 *
 * @property  page        number    Get/set the current page (1-indexed). Setting clamps to [1, total] and re-renders.
 * @property  total       number    Get/set the total page count.
 *
 * @method    next()      Step forward one page (no-op at last page).
 * @method    prev()      Step back one page (no-op at first page).
 * @method    goTo(page)  Jump to an explicit page; clamps to [1, total]; fires `ry:change` only when the page actually changes.
 *
 * @event ry:beforechange
 *        detail: { page: number, oldPage: number, previous: number }
 *        fires:  inside `goTo()` — click, arrow keys, prev/next, `el.page = n` — after clamping and the same-page no-op check, before the `page` attribute is written. `page` is the page ABOUT to become current; `oldPage` / `previous` both carry the current page.
 *        cancelable: preventDefault() stays on the current page; `ry:change` does not fire.
 *
 * @event ry:change
 *        detail: { page: number, oldPage: number }
 *        fires:  after the current page changes (click, key, prev/next, goTo). Setting `page` to the current value is silent.
 *
 * @keyboard  ArrowLeft / ArrowRight — step back / forward by one page (clamped). Focus must be inside the nav.
 *
 * @state     The current page button gets `aria-current="page"` AND `data-ry-state="current"`. Prev / next are decorated with `aria-disabled` and `disabled` at the boundaries.
 *
 * @content   None — the component renders itself from its attributes. Children are ignored.
 *
 * @dom
 *   ry-pagination[total][page][siblings?][boundary?][size?]   role="navigation"   aria-label="Pagination"
 *     > div.ry-pagination__list   [data-ry-target="list"]
 *       > button.ry-pagination__prev   [data-ry-target="prev"]   aria-label="Previous page"
 *       > button.ry-pagination__page   [data-ry-target="page"]   data-page="1"
 *       > span.ry-pagination__ellipsis [data-ry-target="ellipsis"] aria-hidden="true"  "…"
 *       > button.ry-pagination__page   [data-ry-target="page"]   data-page="N"   data-ry-state="current"   aria-current="page"
 *       > span.ry-pagination__ellipsis
 *       > button.ry-pagination__page   [data-ry-target="page"]   data-page="total"
 *       > button.ry-pagination__next   [data-ry-target="next"]   aria-label="Next page"
 *
 * @gotcha  `page` is 1-indexed. A `page="0"` attribute clamps to 1. Avoid the off-by-one trap of treating it as a 0-indexed array.
 *
 * @gotcha  Setting `el.page = currentPage` is a SILENT no-op (it doesn't fire `ry:change`). Listen to `ry:change` for actual transitions, not for every assignment.
 *
 * @gotcha  Ellipses are decorative — they're `<span aria-hidden="true">…</span>` rather than buttons. Selecting all `[data-ry-target="page"]` gives only the real page buttons, never the ellipses.
 *
 * @gotcha  When `total <= boundary * 2 + siblings * 2 + 3` every page fits without ellipses; the renderer just lists them. Don't assume ellipses are always present.
 *
 * @example basic
 * ```html
 * <ry-pagination total="5" page="1"></ry-pagination>
 * ```
 *
 * @example mid-page-with-ellipses
 * ```html
 * <ry-pagination total="20" page="10"></ry-pagination>
 * ```
 *
 * @example wide-siblings
 * ```html
 * <ry-pagination total="20" page="10" siblings="2"></ry-pagination>
 * ```
 *
 * @example wide-boundary
 * ```html
 * <ry-pagination total="20" page="10" boundary="2"></ry-pagination>
 * ```
 *
 * @example small-size
 * ```html
 * <ry-pagination total="10" page="3" size="sm"></ry-pagination>
 * ```
 *
 * @example large-size
 * ```html
 * <ry-pagination total="10" page="3" size="lg"></ry-pagination>
 * ```
 *
 * @tutorial click-page-changes-state-and-fires-event
 * ```js
 * // With total=5 every page button is rendered (no ellipses), so the
 * // third button is page 3. For larger totals where the window jumps,
 * // select by [data-page="N"] instead of index.
 * const el = mount('<ry-pagination total="5" page="1"></ry-pagination>');
 * const events = [];
 * el.addEventListener('ry:change', e => events.push(e.detail));
 * const pageBtns = el.querySelectorAll('[data-ry-target="page"]');
 * pageBtns[2].click();
 * await expectEqual(el.page, 3, 'page attribute updated');
 * await expectEqual(events.length, 1, 'ry:change fired once');
 * await expectEqual(events[0].page, 3, 'detail.page is the new page');
 * await expectEqual(events[0].oldPage, 1, 'detail.oldPage is the previous page');
 * ```
 *
 * @tutorial prev-disabled-at-first-page
 * ```js
 * // Wrong assumption: arrows always work. Prev is disabled at page 1.
 * const el = mount('<ry-pagination total="5" page="1"></ry-pagination>');
 * const prev = el.querySelector('[data-ry-target="prev"]');
 * await expectAttr(prev, 'aria-disabled', 'true');
 * await expectAttr(prev, 'disabled', '');
 * const events = [];
 * el.addEventListener('ry:change', e => events.push(e.detail));
 * prev.click();
 * await expectEqual(el.page, 1, 'still on page 1');
 * await expectEqual(events.length, 0, 'no change event when at boundary');
 * ```
 *
 * @tutorial next-disabled-at-last-page
 * ```js
 * const el = mount('<ry-pagination total="5" page="5"></ry-pagination>');
 * const next = el.querySelector('[data-ry-target="next"]');
 * await expectAttr(next, 'aria-disabled', 'true');
 * await expectAttr(next, 'disabled', '');
 * const events = [];
 * el.addEventListener('ry:change', e => events.push(e.detail));
 * next.click();
 * await expectEqual(el.page, 5, 'still on last page');
 * await expectEqual(events.length, 0);
 * ```
 *
 * @tutorial arrow-keys-step-pages
 * ```js
 * // Wrong assumption: keyboard nav on a navigation landmark is implicit. It
 * // isn't — ArrowLeft / ArrowRight are wired explicitly on ry-pagination.
 * const el = mount('<ry-pagination total="10" page="5"></ry-pagination>');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowRight', bubbles: true }));
 * await expectEqual(el.page, 6, 'ArrowRight advances one page');
 * el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowLeft', bubbles: true }));
 * await expectEqual(el.page, 5, 'ArrowLeft steps back one page');
 * ```
 *
 * @tutorial ellipsis-renders-only-when-needed
 * ```js
 * // Small totals fit entirely — no ellipses.
 * const small = mount('<ry-pagination total="5" page="3"></ry-pagination>');
 * await expectEqual(small.querySelectorAll('[data-ry-target="ellipsis"]').length, 0, 'no ellipsis when every page fits');
 *
 * // Large totals with the current page in the middle — two ellipses.
 * const large = mount('<ry-pagination total="50" page="25"></ry-pagination>');
 * await expectEqual(large.querySelectorAll('[data-ry-target="ellipsis"]').length, 2, 'two ellipses when window is interior');
 * ```
 *
 * @tutorial current-page-has-aria-current
 * ```js
 * const el = mount('<ry-pagination total="10" page="4"></ry-pagination>');
 * const current = el.querySelector('[data-ry-target="page"][aria-current="page"]');
 * await expectEqual(current.dataset.page, '4', 'aria-current marks the active page button');
 * await expectAttr(current, 'data-ry-state', 'current');
 * ```
 *
 * @tutorial siblings-attribute-widens-window
 * ```js
 * // siblings=2 → current ± 2 are always rendered around the active page.
 * const el = mount('<ry-pagination total="20" page="10" siblings="2"></ry-pagination>');
 * const pages = [...el.querySelectorAll('[data-ry-target="page"]')].map(b => Number(b.dataset.page));
 * for (const p of [8, 9, 10, 11, 12]) {
 *   await expectEqual(pages.includes(p), true, 'page ' + p + ' is in the window');
 * }
 * ```
 *
 * @tutorial programmatic-setter-fires-once
 * ```js
 * const el = mount('<ry-pagination total="10" page="1"></ry-pagination>');
 * const events = [];
 * el.addEventListener('ry:change', e => events.push(e.detail));
 * el.page = 7;
 * await expectEqual(el.page, 7);
 * await expectEqual(events.length, 1, 'fires when value changes');
 * el.page = 7;
 * await expectEqual(events.length, 1, 'silent when value is unchanged');
 * ```
 *
 * @tutorial beforechange-veto
 * ```js
 * // preventDefault() on ry:beforechange stays on the current page and
 * // ry:change stays silent — e.g. "finish editing this row before paging".
 * const el = mount('<ry-pagination total="10" page="1"></ry-pagination>');
 * const changed = [];
 * el.addEventListener('ry:change', e => changed.push(e.detail.page));
 * el.addEventListener('ry:beforechange', e => {
 *   if (e.detail.page === 2 && e.detail.previous === 1) e.preventDefault();
 * }, { once: true });
 * el.next();
 * await expectEqual(el.page, 1, 'vetoed step stays on page 1');
 * await expectEqual(changed.length, 0, 'ry:change did not fire');
 * el.next();
 * await expectEqual(el.page, 2, 'without the veto, next() advances');
 * await expectEqual(changed[0], 2, 'ry:change fired with the new page');
 * ```
 *
 * @see ry-button
 * @see ry-button-group
 */

import { RyElement } from '../core/ry-element.js';

interface PaginationChangeDetail {
  page: number;
  oldPage: number;
}

interface PaginationBeforeChangeDetail extends PaginationChangeDetail {
  previous: number;
}

type PaginationItem = number | 'ellipsis';

export class RyPagination extends RyElement {
  static get observedAttributes(): string[] {
    return ['total', 'page', 'siblings', 'boundary', 'size'];
  }

  setup(): void {
    if (!this.hasAttribute('role')) this.setAttribute('role', 'navigation');
    if (!this.hasAttribute('aria-label')) this.setAttribute('aria-label', 'Pagination');
    this.#render();
    this.on(this, 'keydown', this.#handleKeydown);
  }

  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue) return;
    if (!this.hasAttribute('data-ry-initialized')) return;
    if (name === 'page') {
      // Setter reflection already clamped — just re-render.
      this.#render();
    } else {
      this.#render();
    }
  }

  // ─── Public API ────────────────────────────────────────────────────

  get total(): number {
    const raw = parseInt(this.getAttribute('total') ?? '', 10);
    return Number.isFinite(raw) && raw >= 1 ? raw : 1;
  }
  set total(value: number) {
    this.setAttribute('total', String(Math.max(1, Math.floor(value))));
  }

  get page(): number {
    const raw = parseInt(this.getAttribute('page') ?? '', 10);
    const total = this.total;
    if (!Number.isFinite(raw)) return 1;
    return Math.min(Math.max(1, raw), total);
  }
  set page(value: number) {
    this.goTo(value);
  }

  get siblings(): number {
    const raw = parseInt(this.getAttribute('siblings') ?? '', 10);
    return Number.isFinite(raw) && raw >= 0 ? raw : 1;
  }

  get boundary(): number {
    const raw = parseInt(this.getAttribute('boundary') ?? '', 10);
    return Number.isFinite(raw) && raw >= 0 ? raw : 1;
  }

  next(): void {
    this.goTo(this.page + 1);
  }

  prev(): void {
    this.goTo(this.page - 1);
  }

  goTo(target: number): void {
    const oldPage = this.page;
    const clamped = Math.min(Math.max(1, Math.floor(target)), this.total);
    if (clamped === oldPage) return;
    if (!this.emit<PaginationBeforeChangeDetail>('beforechange', { page: clamped, oldPage, previous: oldPage })) return;
    // setAttribute will trigger attributeChangedCallback → re-render.
    this.setAttribute('page', String(clamped));
    this.emit<PaginationChangeDetail>('change', { page: clamped, oldPage });
  }

  // ─── Rendering ─────────────────────────────────────────────────────

  /**
   * Compute the visible items: page numbers + 'ellipsis' markers.
   * Mirrors the canonical pagination layout used by daisyUI demos / MUI /
   * shadcn: first boundary pages, optional ellipsis, current ± siblings,
   * optional ellipsis, last boundary pages.
   */
  #computeItems(): PaginationItem[] {
    const total = this.total;
    const page = this.page;
    const siblings = this.siblings;
    const boundary = this.boundary;

    // Total slots if we showed everything: boundaries + window + 2 ellipses.
    const minVisible = boundary * 2 + siblings * 2 + 3;
    if (total <= minVisible) {
      // Just list every page.
      const all: PaginationItem[] = [];
      for (let i = 1; i <= total; i++) all.push(i);
      return all;
    }

    const startBoundary: number[] = [];
    for (let i = 1; i <= Math.min(boundary, total); i++) startBoundary.push(i);

    const endBoundary: number[] = [];
    for (let i = Math.max(total - boundary + 1, 1); i <= total; i++) endBoundary.push(i);

    const windowStart = Math.max(page - siblings, boundary + 2);
    const windowEnd = Math.min(page + siblings, total - boundary - 1);

    const items: PaginationItem[] = [...startBoundary];

    if (windowStart > boundary + 2) {
      items.push('ellipsis');
    } else if (boundary + 1 < windowStart) {
      // Adjacent — fill the gap with the literal page number instead of '…'.
      items.push(boundary + 1);
    }

    for (let i = windowStart; i <= windowEnd; i++) items.push(i);

    if (windowEnd < total - boundary - 1) {
      items.push('ellipsis');
    } else if (windowEnd + 1 < total - boundary + 1) {
      items.push(total - boundary);
    }

    items.push(...endBoundary);

    // Dedupe (boundaries can overlap the window in tight configs).
    const seen = new Set<number>();
    const out: PaginationItem[] = [];
    for (const it of items) {
      if (it === 'ellipsis') {
        if (out[out.length - 1] !== 'ellipsis') out.push(it);
      } else if (!seen.has(it)) {
        seen.add(it);
        out.push(it);
      }
    }
    return out;
  }

  #render(): void {
    const total = this.total;
    const page = this.page;
    const items = this.#computeItems();

    const list = document.createElement('div');
    list.setAttribute('data-ry-target', 'list');
    list.className = 'ry-pagination__list';

    // Prev button
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.setAttribute('data-ry-target', 'prev');
    prev.className = 'ry-pagination__prev';
    prev.setAttribute('aria-label', 'Previous page');
    prev.textContent = '‹'; // ‹
    if (page <= 1) {
      prev.setAttribute('aria-disabled', 'true');
      prev.setAttribute('disabled', '');
    }
    list.appendChild(prev);

    for (const item of items) {
      if (item === 'ellipsis') {
        const span = document.createElement('span');
        span.setAttribute('data-ry-target', 'ellipsis');
        span.className = 'ry-pagination__ellipsis';
        span.setAttribute('aria-hidden', 'true');
        span.textContent = '…'; // …
        list.appendChild(span);
      } else {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.setAttribute('data-ry-target', 'page');
        btn.className = 'ry-pagination__page';
        btn.dataset.page = String(item);
        btn.setAttribute('aria-label', `Go to page ${item}`);
        btn.textContent = String(item);
        if (item === page) {
          btn.setAttribute('aria-current', 'page');
          btn.dataset.ryState = 'current';
        }
        list.appendChild(btn);
      }
    }

    // Next button
    const next = document.createElement('button');
    next.type = 'button';
    next.setAttribute('data-ry-target', 'next');
    next.className = 'ry-pagination__next';
    next.setAttribute('aria-label', 'Next page');
    next.textContent = '›'; // ›
    if (page >= total) {
      next.setAttribute('aria-disabled', 'true');
      next.setAttribute('disabled', '');
    }
    list.appendChild(next);

    // Replace previous render
    this.replaceChildren(list);

    // Wire click handlers (on() auto-cleans on disconnect)
    this.on(prev, 'click', this.#handlePrev);
    this.on(next, 'click', this.#handleNext);
    list.querySelectorAll<HTMLButtonElement>('[data-ry-target="page"]').forEach(btn => {
      this.on(btn, 'click', this.#handlePageClick);
    });
  }

  #handlePrev = (event: Event): void => {
    const t = event.currentTarget as HTMLButtonElement;
    if (t.hasAttribute('disabled')) return;
    this.prev();
  };

  #handleNext = (event: Event): void => {
    const t = event.currentTarget as HTMLButtonElement;
    if (t.hasAttribute('disabled')) return;
    this.next();
  };

  #handlePageClick = (event: Event): void => {
    const btn = event.currentTarget as HTMLButtonElement;
    const target = Number(btn.dataset.page);
    if (Number.isFinite(target)) this.goTo(target);
  };

  #handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.prev();
    }
  };
}

customElements.define('ry-pagination', RyPagination);
